import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faPlus,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ClockIcon,
  DocumentCheckIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";

import { useTable, usePagination } from "react-table";
import clsx from "clsx";
// Note: Assuming a similar modal for view, but simplified. Create PlanCoverageViewModal if needed.
import CorporateViewModal from "../Corporate/CorporateViewModal"; // Adapt or rename to PlanCoverageViewModal later
// import AddPlanModal from "./AddPlanModal";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const PlanAndCoverageBenefitsTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("corp_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const [finalizeStatusFilter, setFinalizeStatusFilter] = useState("Pending"); // New filter for finalize_status

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCorp, setSelectedCorp] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewCorpCode, setViewCorpCode] = useState(null);

  const [viewCorpCodeFormModal, setViewCorpCodeFormModal] = useState(null);
  const [viewCorpNameFormModal, setViewCorpNameFormModal] = useState(null);

  // Removed unused states like finalizeModalOpen, addFundModalOpen, etc., as per specs

  const statusMap = {
    1: { name: "Active", color: "green" },
    2: { name: "Terminated/Cancelled", color: "red" },
    3: { name: "Suspended", color: "yellow" },
    4: { name: "Expired", color: "slate" },
    default: { name: "Unknown", color: "gray" },
  };

  const finalizeStatusMap = {
    finalized: { name: "Finalized", color: "green" },
    pending: { name: "Pending", color: "yellow" },
    default: { name: "Unknown", color: "gray" },
  };
  const mainSubStatusMap = {
    main: { name: "Main Corp", color: "blue" },
    sub: { name: "Sub Corp", color: "yellow" },
    default: { name: "Unknown", color: "gray" },
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchPlanCoverages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/plan-coverages`, {
        // New API endpoint
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
          finalize_status:
            finalizeStatusFilter === "ALL"
              ? undefined
              : finalizeStatusFilter.toLowerCase(), // Adapted filter
        },
      });
      setData(response.data.plan_coverages || []); // Assuming response structure similar, but renamed
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error("Failed to fetch plan & coverage setups.", {
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    sortBy,
    sortDirection,
    finalizeStatusFilter,
  ]);

  useEffect(() => {
    fetchPlanCoverages();
  }, [fetchPlanCoverages]);

  // Removed fetchCorpPrefixes, fetchFundHistory, etc., as not needed

  const openModal = (corp) => {
    setSelectedCorp(corp);
    setNewStatus("");
    setReason("");
    setFiles([]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCorp(null);
    setNewStatus("");
    setReason("");
    setFiles([]);
    setModalLoading(false);
  };

  // Removed openAddFundModal, closeAddFundModal, etc.

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        if (isModalOpen) {
          closeModal();
        }
      }
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isModalOpen]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 5);
    setFiles(selectedFiles);
  };

  const uploadFile = async (file, corpCode) => {
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("uploader_type", "status_change");
    formDataUpload.append("corporate_code", corpCode);

    try {
      const response = await axios.post(
        `${BASE_URL}/plan-coverages/document/upload`, // New API
        formDataUpload,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.upload_id;
    } catch (error) {
      throw new Error(
        "Failed to upload file: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleChangeStatus = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error("Reason is required.");
      return;
    }
    if (!newStatus) {
      toast.error("Please select a new status.");
      return;
    }

    setModalLoading(true);
    try {
      const uploadIds = await Promise.all(
        files.map((file) => uploadFile(file, selectedCorp.corp_code))
      );

      await axios.put(
        `${BASE_URL}/plan-coverages/${selectedCorp.corp_code}`, // New API
        { status_flag: newStatus },
        { headers: getAuthHeaders() }
      );

      await axios.post(
        `${BASE_URL}/plan-coverage-movements`, // New API (adapted from corporate-movements)
        {
          corporate_code: selectedCorp.corp_code,
          corporate_flag: newStatus,
          effectivity_date: new Date().toISOString().split("T")[0],
          reason: reason,
          remarks: reason,
          status: 1,
          lastupdate_date: new Date().toISOString(),
          update_by: "Admin",
          upload_ids: uploadIds,
        },
        { headers: getAuthHeaders() }
      );

      toast.success("Status updated successfully.");
      closeModal();
      fetchPlanCoverages();
    } catch (error) {
      toast.error(
        "Failed to update status: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setModalLoading(false);
    }
  };

  // Removed handleAddFund, confirmAddFund, handleExportFundHistory, etc.

  const columns = useMemo(
    () => [
      {
        Header: "Corp Code", // Assuming similar fields; adapt if needed
        accessor: "corp_code",
        Cell: ({ value }) => (
          <span className="font-medium text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Corp Prefix",
        accessor: "corp_prefix",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Corp Name",
        accessor: "corp_name",
        Cell: ({ value }) => (
          <span className="text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Location",
        accessor: "location",
        Cell: ({ value }) => (
          <span className="text-gray-600">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Contact Person",
        accessor: "contact_person",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Main/Sub Corp Type",
        accessor: "main_sub",
        Cell: ({ value }) => {
          const status = mainSubStatusMap[value] || mainSubStatusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Finalize Status",
        accessor: "finalize_status",
        Cell: ({ value }) => {
          const status = finalizeStatusMap[value] || finalizeStatusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => {
          const status = statusMap[value] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex flex-row gap-1">
            {/* <button
              onClick={() => setViewCorpCode(row.original.corp_code)}
              title="View Plan"
              // className="inline-flex items-center space-x-1 rounded-md px-2 py-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800 whitespace-nowrap"

              className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 
                   bg-gradient-to-r from-blue-500 to-blue-600 
                   text-white shadow-md transition-all duration-200 
                   hover:from-blue-600 hover:to-blue-700 
                   hover:scale-105 active:scale-95 whitespace-nowrap"
            >
              <EyeIcon className="h-5 w-5" />
            </button> */}

            <button
              title="Edit/View Manage Plans"
              onClick={() =>
                navigate(`/plan-and-coverage/${row.original.corp_code}`)
              }
              className="inline-flex items-center space-x-1 rounded-md px-2 py-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800 whitespace-nowrap"
              // className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 
              //      bg-gradient-to-r from-blue-500 to-blue-600 
              //      text-white shadow-md transition-all duration-200 
              //      hover:from-blue-600 hover:to-blue-700 
              //      hover:scale-105 active:scale-95 whitespace-nowrap"
            >
              {/* <Cog6ToothIcon className="h-5 w-5" /> */}
              <EyeIcon className="h-5 w-5" />
            </button>

            {row.original.finalize_status === "pending" && (
              <button
                // This will redirect to new page
                onClick={() =>
                  navigate(
                    `/plan-and-coverage-benefits-setup/add/${row.original.corp_code}`
                  )
                }
                // This will open modal
                // onClick={() => {
                //   setViewCorpCodeFormModal(row.original.corp_code);
                //   setViewCorpNameFormModal(row.original.corp_name)
                // }}
                title="Add New Plan"
                className="inline-flex items-center space-x-1 rounded-md px-2 py-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800 whitespace-nowrap"
                // className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 
                //      bg-gradient-to-r from-green-500 to-green-600 
                //      text-white shadow-md transition-all duration-200 
                //      hover:from-green-600 hover:to-green-700 
                //      hover:scale-105 active:scale-95 whitespace-nowrap"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            )}

            {/* <button
            title="Manage Plans"
              onClick={() =>
                navigate(`/plan-and-coverage/${row.original.corp_code}`)
              }
              className="text-indigo-600 hover:text-indigo-800"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button> */}
            {/* <button
              title="Manage Plans"
              onClick={() => navigate(`/plan-and-coverage/${row.original.corp_code}`)}
              className="text-indigo-600 hover:text-indigo-800"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button> */}
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: currentPage - 1, pageSize: itemsPerPage },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / itemsPerPage),
        disableSortRemove: true,
      },
      usePagination
    );

  const handleSort = (column) => {
    if (!column || !column.id) return;
    if (sortBy === column.id) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column.id);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  return (
    <div className="mx-auto w-full max-w-1xl p-4">
      <ToastContainer />

      {/* Toolbar */}
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
              <FontAwesomeIcon icon={faShieldAlt} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Plan & Coverage Setup</h1>
              <div className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record
                {totalCount === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          {/* <button
            onClick={() => navigate("/plan-coverage/add-new")}
            className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add New Plan & Coverage Setup</span>
          </button> */}
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search plan & coverages..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={finalizeStatusFilter}
            onChange={(e) => {
              setFinalizeStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Finalized">Finalized</option>
          </select>

          <div className="hidden items-center justify-end text-sm text-gray-600 lg:flex">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {[
                  "Corp Code",
                  "Corp Prefix",
                  "Corp Name",
                  "Location",
                  "Contact Person",
                  "Main/Sub Corp Type",
                  "Finalize Status",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <tr key={`head-${h}`}>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      {h}
                    </th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {[...Array(8)].map((_, idx) => (
                  <tr key={idx} className="border-t">
                    {[...Array(9)].map((__, i) => (
                      <td key={i} className="px-4 py-3">
                        <Skeleton height={16} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : data.length === 0 ? (
            <div className="p-8">
              <EmptyState
                title="No plan & coverages found"
                subtitle="Try changing your filters or search."
              />
            </div>
          ) : (
            <table
              {...getTableProps()}
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody
                {...getTableBodyProps()}
                className="divide-y divide-gray-100"
              >
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {row.cells.map((cell) => (
                        <td
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          className="px-4 py-3 text-sm text-gray-800"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
        <div className="text-xs text-gray-600">
          Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
          {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
          {totalCount.toLocaleString()} entries
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
            First
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={clsx(
                "rounded-md px-2 py-1 text-xs",
                currentPage === page
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Next
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Last
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>

          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status Change Modal - Kept for now, but can remove if not needed */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">
                  Change Status for {selectedCorp?.corp_name}
                </h2>
                <div className="text-xs text-white/80">
                  {selectedCorp?.corp_code}
                </div>
              </div>

              <form onSubmit={handleChangeStatus} className="space-y-4 p-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    New Status *
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">Select new status</option>
                    {Object.entries(statusMap).map(([key, { name }]) =>
                      key !== "default" &&
                      key !== selectedCorp.status_flag?.toString() ? (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ) : null
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Reason *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    placeholder="Enter reason for status change"
                    className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Attach Files (Optional, max 5)
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {files.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${i}`}
                          className="truncate text-xs text-gray-600"
                        >
                          • {f.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                    disabled={modalLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={modalLoading}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {modalLoading ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Updating…
                      </>
                    ) : (
                      "Update Status"
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Modal - Adapted */}
      <AnimatePresence>
        {viewCorpCode && (
          <CorporateViewModal // Rename to PlanCoverageViewModal
            corpCode={viewCorpCode}
            onClose={() => setViewCorpCode(null)}
          />
        )}
      </AnimatePresence>

      {/* View Modal - Adapted */}
      {/* <AnimatePresence>
        {viewCorpCodeFormModal && (
          <AddPlanModal
            corpCode={viewCorpCodeFormModal}
            onClose={() => setViewCorpCodeFormModal(null)}
            corpName={viewCorpNameFormModal}
          />
        )}
      </AnimatePresence> */}
    </div>
  );
};

export default PlanAndCoverageBenefitsTable;
